'use client';

import { useCallback, useEffect, useState } from 'react';
import { getDb } from '@/lib/db';
import { formatMonthKey } from '@/lib/date';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import { SavingsGoal } from '@/lib/types';

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function currentMonth() {
  return formatMonthKey(new Date());
}

export function useSavingsGoal(month = currentMonth()) {
  const [goal, setGoal] = useState<SavingsGoal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setGoal((await db.savingsGoals.where('month').equals(month).first()) ?? null);
      setError(null);
    } catch (err) {
      setError(storageErrorMessage("Impossible de charger l'objectif d'épargne.", err));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const saveGoal = useCallback(async (amount: number) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      const now = new Date().toISOString();
      const existing = await db.savingsGoals.where('month').equals(month).first();

      if (existing) {
        await db.savingsGoals.update(existing.id, { amount, updatedAt: now });
      } else {
        await db.savingsGoals.add({
          id: `goal-${month}`,
          month,
          amount,
          createdAt: now,
          updatedAt: now,
        });
      }
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible d'enregistrer l'objectif d'épargne.", err));
    }
  }, [month]);

  const resetData = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.savingsGoals.clear();
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible de réinitialiser l'objectif d'épargne.", err));
    }
  }, []);

  return {
    goal,
    loading,
    error,
    saveGoal,
    resetData,
  };
}
